import * as i0 from '@angular/core';
import { Component, Input, NgModule } from '@angular/core';

class Color {
    constructor(r, g, b, a = 0xFF) {
        this.R = 0;
        this.G = 0;
        this.B = 0;
        this.A = 0xFF;
        this.R = r;
        this.G = g;
        this.B = b;
        this.A = a;
    }
    toHexString() {
        return `#${pad(this.R.toString(16))}${pad(this.G.toString(16))}${pad(this.B.toString(16))}`;
    }
    static fromHexString(value) {
        let r, g, b;
        if (!value || value[0] != '#' || (value.length != 7 && value.length != 4)) {
            throw new Error('String is not a hex color value.');
        }
        if (value.length === 7) {
            r = value.substring(1, 3);
            g = value.substring(3, 5);
            b = value.substring(5, 7);
            return new Color(parseInt(r, 16), parseInt(g, 16), parseInt(b, 16));
        }
        else {
            r = value[1];
            g = value[2];
            b = value[3];
            return new Color(parseInt(r + r, 16), parseInt(g + g, 16), parseInt(b + b, 16));
        }
    }
}
function pad(num, size = 2) {
    let result = num + '';
    while (result.length < size)
        result = '0' + result;
    return result;
}

class TXSettings {
    constructor(WebSocketURL = '') {
        this.WebSocketURL = WebSocketURL;
        this.IsMVC = false;
        this.IsNodeJs = true;
        this.NoPrintEndpoint = true;
        this.NoFileSystemAccess = true; // No file system access on the web server
        this.replaceContainer = true;
        this.containerID = '__txWebControlTempContainer';
        this.ConnectionID = '';
        this.DocumentTargetMarkers = true;
        this.BackColor = new Color(0xFF, 0xFF, 0xFF);
        this.StatusBarColor = new Color(0x2B, 0x56, 0x9A);
        this.EditMode = 1;
        this.ContextMenusEnabled = true;
        this.SaveDocumentQuestionDialogEnabled = true;
        this.TableGridLines = true;
        this.TextFrameMarkerLines = true;
        this.ControlChars = false;
        this.TextFieldsEditable = false;
        this.FormattingPrinter = '';
        this.Culture = '';
        this.UICulture = '';
        this.UserNames = [];
        this.ReconnectTimeout = 0;
    }
    static editModeFromString(value) {
        switch (value) {
            case 'Edit': return 1;
            case 'ReadAndSelect': return 2;
            case 'ReadOnly': return 3;
            case 'UsePassword': return 2048;
        }
        return 1; // EditMode.Edit
    }
}

class DocumentEditorComponent {
    constructor(hostElement) {
        this.hostElement = hostElement;
        this.documentTargetMarkers = DocumentEditorComponent.DefaultSettings.DocumentTargetMarkers;
        this.backColor = DocumentEditorComponent.DefaultSettings.BackColor.toHexString();
        this.statusBarColor = DocumentEditorComponent.DefaultSettings.StatusBarColor.toHexString();
        this.editMode = 'Edit';
        this.contextMenusEnabled = DocumentEditorComponent.DefaultSettings.ContextMenusEnabled;
        this.saveDocumentQuestionDialogEnabled = DocumentEditorComponent.DefaultSettings.SaveDocumentQuestionDialogEnabled;
        this.tableGridLines = DocumentEditorComponent.DefaultSettings.TableGridLines;
        this.textFrameMarkerLines = DocumentEditorComponent.DefaultSettings.TextFrameMarkerLines;
        this.controlChars = DocumentEditorComponent.DefaultSettings.ControlChars;
        this.textFieldsEditable = DocumentEditorComponent.DefaultSettings.TextFieldsEditable;
        this.formattingPrinter = DocumentEditorComponent.DefaultSettings.FormattingPrinter;
        this.culture = DocumentEditorComponent.DefaultSettings.Culture;
        this.uiCulture = DocumentEditorComponent.DefaultSettings.UICulture;
        this.userNames = DocumentEditorComponent.DefaultSettings.UserNames;
        this.reconnectTimeout = DocumentEditorComponent.DefaultSettings.ReconnectTimeout;
        this.webSocketURL = DocumentEditorComponent.DefaultSettings.WebSocketURL;
        // Generate connection id
        this.connectionID = generateRandomID(21);
    }
    ngOnInit() {
        let self = this;
        // Throw error if WebSocketURL is not specified
        if (!this.webSocketURL) {
            throw new Error('Please provide a WebSocket URL.');
        }
        // Add TX Text Control script to host element
        let httpUrl = this.webSocketURL.replace(/^ws/, 'http');
        // Split URL into path and query string
        let url = new URL(httpUrl);
        httpUrl = url.origin + url.pathname;
        let queryString = url.searchParams.toString();
        let elemScript = document.createElement('script');
        elemScript.onload = function () {
            // Call TXTextControl.init() with settings
            let elemScriptInit = document.createElement('script');
            elemScriptInit.type = 'module';
            elemScriptInit.text = `
            // Initialize TX Text Control
            TXTextControl.init(${JSON.stringify(self.settings)});
            // Fire "txDocumentEditorLoaded" event to notify that the main TXTextControl object is ready
            var evt = new CustomEvent("txDocumentEditorLoaded", { detail: { /* Empty event argument */ } })
            document.dispatchEvent(evt);
         `;
            self.hostElement.nativeElement.appendChild(elemScriptInit);
        };
        // Call the GetResource endpoint with the name of the script and append a possible query string
        elemScript.src = `${httpUrl}/GetResource?name=tx-document-editor.min.js${(queryString ? `&${queryString}` : '')}`;
        this.hostElement.nativeElement.appendChild(elemScript);
        // Set stuff on host element
        this.hostElement.nativeElement.style.display = 'inline-block';
        this.hostElement.nativeElement.style.width = this.width;
        this.hostElement.nativeElement.style.height = this.height;
    }
    get settings() {
        let settings = new TXSettings(this.webSocketURL);
        settings.ConnectionID = this.connectionID;
        settings.DocumentTargetMarkers = this.documentTargetMarkers;
        settings.BackColor = Color.fromHexString(this.backColor);
        settings.StatusBarColor = Color.fromHexString(this.statusBarColor);
        settings.EditMode = TXSettings.editModeFromString(this.editMode);
        settings.ContextMenusEnabled = this.contextMenusEnabled;
        settings.SaveDocumentQuestionDialogEnabled = this.saveDocumentQuestionDialogEnabled;
        settings.TableGridLines = this.tableGridLines;
        settings.TextFrameMarkerLines = this.textFrameMarkerLines;
        settings.ControlChars = this.controlChars;
        settings.TextFieldsEditable = this.textFieldsEditable;
        settings.FormattingPrinter = this.formattingPrinter;
        settings.Culture = this.culture;
        settings.UICulture = this.uiCulture;
        settings.UserNames = this.userNames;
        settings.ReconnectTimeout = this.reconnectTimeout;
        settings.WebSocketURL = this.webSocketURL;
        return settings;
    }
}
DocumentEditorComponent.DefaultSettings = new TXSettings();
DocumentEditorComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: DocumentEditorComponent, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Component });
DocumentEditorComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: DocumentEditorComponent, selector: "tx-document-editor", inputs: { width: "width", height: "height", documentTargetMarkers: "documentTargetMarkers", backColor: "backColor", statusBarColor: "statusBarColor", editMode: "editMode", contextMenusEnabled: "contextMenusEnabled", saveDocumentQuestionDialogEnabled: "saveDocumentQuestionDialogEnabled", tableGridLines: "tableGridLines", textFrameMarkerLines: "textFrameMarkerLines", controlChars: "controlChars", textFieldsEditable: "textFieldsEditable", formattingPrinter: "formattingPrinter", culture: "culture", uiCulture: "uiCulture", userNames: "userNames", reconnectTimeout: "reconnectTimeout", webSocketURL: "webSocketURL" }, ngImport: i0, template: "<div id=\"__txWebControlTempContainer\" style=\"display:inline-block;position:relative;width:100%;height:100%\">\r\n</div>\r\n", styles: [""] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: DocumentEditorComponent, decorators: [{
            type: Component,
            args: [{ selector: 'tx-document-editor', template: "<div id=\"__txWebControlTempContainer\" style=\"display:inline-block;position:relative;width:100%;height:100%\">\r\n</div>\r\n" }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }]; }, propDecorators: { width: [{
                type: Input
            }], height: [{
                type: Input
            }], documentTargetMarkers: [{
                type: Input
            }], backColor: [{
                type: Input
            }], statusBarColor: [{
                type: Input
            }], editMode: [{
                type: Input
            }], contextMenusEnabled: [{
                type: Input
            }], saveDocumentQuestionDialogEnabled: [{
                type: Input
            }], tableGridLines: [{
                type: Input
            }], textFrameMarkerLines: [{
                type: Input
            }], controlChars: [{
                type: Input
            }], textFieldsEditable: [{
                type: Input
            }], formattingPrinter: [{
                type: Input
            }], culture: [{
                type: Input
            }], uiCulture: [{
                type: Input
            }], userNames: [{
                type: Input
            }], reconnectTimeout: [{
                type: Input
            }], webSocketURL: [{
                type: Input
            }] } });
const ConIDCharset = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ1234567890_';
/**
 * Generates a random string with a given length.
 * @param length Length of the generated ID in characters.
 */
function generateRandomID(length) {
    if (length < 1)
        throw new Error('ID must be at least one character long.');
    let result = '';
    for (var i = 0; i < length; ++i) {
        result += ConIDCharset.charAt(Math.floor(Math.random() * ConIDCharset.length));
    }
    return result;
}

class DocumentEditorModule {
}
DocumentEditorModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: DocumentEditorModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
DocumentEditorModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.10", ngImport: i0, type: DocumentEditorModule, declarations: [DocumentEditorComponent], exports: [DocumentEditorComponent] });
DocumentEditorModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: DocumentEditorModule });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: DocumentEditorModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [DocumentEditorComponent],
                    imports: [],
                    exports: [DocumentEditorComponent]
                }]
        }] });

/*
 * Public API Surface of tx-ng-document-editor
 */

/**
 * Generated bundle index. Do not edit.
 */

export { DocumentEditorComponent, DocumentEditorModule };
